<template>
  <div :class="['security-page', { 'central-focus': tab === 0, 'territory-focus': tab === 1 }]">
    <section class="resource-toolbar">
      <div>
        <h2>{{ activeSectionMeta.title }}</h2>
        <p>{{ activeSectionMeta.description }}</p>
      </div>
      <div v-if="tab === 0" class="toolbar-actions">
        <v-select
          v-model="statusFilter"
          :items="statusOptions"
          dense
          outlined
          hide-details
          class="status-filter"
          @change="loadAlerts"
        />
        <v-btn outlined color="primary" @click="enableSound">
          <v-icon left size="18">mdi-volume-high</v-icon>
          Ativar som
        </v-btn>
      </div>
      <v-btn v-if="tab === 2" text color="primary" @click="openTeamDialog()">
        <v-icon left size="18">mdi-plus</v-icon>
        Adicionar
      </v-btn>
    </section>

    <div class="tabs-body">
      <template v-if="tab === 0">
        <section class="central-grid">
          <div class="map-panel">
            <div class="central-map-badge">
              <v-icon size="16" color="primary">mdi-shield-alert-outline</v-icon>
              <span>Central ativa</span>
            </div>
            <div ref="alertsMap" class="map"></div>
          </div>

          <aside :class="['queue-panel', { minimized: queueMinimized }]">
            <button v-if="queueMinimized" class="queue-minimized-button" type="button" @click="queueMinimized = false">
              <v-icon size="18" color="primary">mdi-format-list-bulleted</v-icon>
              <span>Fila</span>
              <strong>{{ displayAlerts.length }}</strong>
            </button>

            <template v-else>
            <div class="queue-header">
              <div>
                <strong>Fila de chamados</strong>
                <span>{{ displayAlerts.length }} chamado(s)</span>
              </div>
              <div class="queue-header-actions">
                <v-btn icon :loading="loadingAlerts" @click="loadAlerts">
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
                <v-btn icon :title="queueMinimized ? 'Expandir fila' : 'Minimizar fila'" @click="queueMinimized = !queueMinimized">
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
              </div>
            </div>

            <div class="queue-controls">
              <v-select
                v-model="statusFilter"
                :items="statusOptions"
                dense
                outlined
                hide-details
                class="queue-status-filter"
                @change="loadAlerts"
              />
              <v-btn depressed color="primary" class="queue-sound-btn" @click="enableSound">
                <v-icon left size="18">mdi-volume-high</v-icon>
                Som
              </v-btn>
            </div>

            <article v-for="alert in displayAlerts" :key="alert.id" class="alert-card" :class="`priority-${alert.priority}`">
              <div class="alert-top">
                <strong>{{ alert.title }}</strong>
                <span>{{ statusLabel(alert.status) }}</span>
              </div>
              <p>{{ alert.location_address || "Localização não informada" }}</p>
              <small>{{ alert.created_at | defaultDate }}</small>
              <div class="alert-actions">
                <v-btn x-small depressed color="primary" @click="assign(alert)">Assumir</v-btn>
                <v-btn x-small outlined color="primary" @click="setStatus(alert, 'on_route')">Deslocamento</v-btn>
                <v-btn x-small outlined color="primary" @click="drawRoute(alert)">Rota</v-btn>
                <v-btn x-small outlined color="primary" @click="setStatus(alert, 'resolved')">Resolver</v-btn>
              </div>
            </article>

            <div v-if="!displayAlerts.length" class="empty-queue">
              <v-icon color="primary">mdi-shield-check-outline</v-icon>
              <strong>Nenhum alerta ativo</strong>
              <span>Novos chamados aparecerão aqui e no mapa.</span>
            </div>
            </template>
          </aside>
        </section>
      </template>

      <template v-if="tab === 1">
        <section class="territory-map-stage">
          <div class="territory-map-wrap">
            <div class="map-search-control">
              <v-text-field
                v-model="addressSearch"
                dense
                solo
                flat
                hide-details
                placeholder="Buscar rua, bairro ou cidade"
                prepend-inner-icon="mdi-map-search-outline"
                @keydown.enter="searchAddress"
              />
              <v-btn icon :loading="searchingAddress" @click="searchAddress">
                <v-icon>mdi-magnify</v-icon>
              </v-btn>
              <div v-if="addressResults.length" class="floating-results">
                <button
                  v-for="result in addressResults"
                  :key="result.place_id"
                  type="button"
                  @click="moveToAddress(result)"
                >
                  <v-icon size="17">mdi-map-marker-outline</v-icon>
                  <span>{{ result.display_name }}</span>
                </button>
              </div>
            </div>

            <div class="map-tool-stack">
              <button :class="{ active: drawingTerritory }" type="button" title="Adicionar pontos" @click="drawingTerritory = !drawingTerritory">
                <v-icon size="20">{{ drawingTerritory ? "mdi-vector-polygon" : "mdi-vector-polyline" }}</v-icon>
              </button>
              <button type="button" title="Desfazer ponto" :disabled="!territoryPoints.length" @click="undoTerritoryPoint">
                <v-icon size="20">mdi-undo</v-icon>
              </button>
              <button type="button" title="Enquadrar território" :disabled="!territoryPoints.length" @click="fitTerritory">
                <v-icon size="20">mdi-fit-to-page-outline</v-icon>
              </button>
              <button class="danger" type="button" title="Limpar território" :disabled="!territoryPoints.length" @click="clearTerritory">
                <v-icon size="20">mdi-trash-can-outline</v-icon>
              </button>
            </div>

            <div class="map-points-chip">
              <strong>{{ territoryPoints.length }}</strong>
              <span>pontos</span>
            </div>

            <div class="map-floating-hint">
              <v-icon size="18" color="primary">mdi-cursor-default-click-outline</v-icon>
              <span>{{ drawingTerritory ? "Clique no mapa para adicionar pontos. Arraste para ajustar." : "Desenho pausado." }}</span>
            </div>
            <div ref="territoryMap" class="territory-map"></div>
          </div>
        </section>
      </template>

      <template v-if="tab === 2">
        <ListFilters
          v-model="teamFilterDraft"
          :fields="teamFilterFields"
          :loading="loadingTeams"
          @filter="applyTeamFilters"
          @clear="applyTeamFilters"
        />

        <section class="teams-panel">
          <BoaruaDataTable
            :headers="teamHeaders"
            :items="filteredTeams"
            :loading="loadingTeams"
            item-key="id"
            show-expand
            single-expand
            class="boarua-table"
          >
            <template v-slot:item.groups="{ item }">
              <div class="team-groups">
                <v-chip
                  v-for="group in teamGroups(item)"
                  :key="group.id"
                  x-small
                  label
                  color="primary"
                  text-color="white"
                >
                  {{ group.name }}
                </v-chip>
                <span v-if="!teamGroups(item).length">Todos os núcleos</span>
              </div>
            </template>
            <template v-slot:item.users="{ item }">
              {{ item.users ? item.users.length : 0 }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn icon small title="Adicionar usuário" @click="openOperatorDialog(item)">
                <v-icon size="19">mdi-account-plus-outline</v-icon>
              </v-btn>
              <v-btn icon small @click="openTeamDialog(item)"><v-icon size="19">mdi-pencil-outline</v-icon></v-btn>
              <v-btn icon small color="error" title="Excluir equipe" @click="openDeleteTeamDialog(item)">
                <v-icon size="19">mdi-trash-can-outline</v-icon>
              </v-btn>
            </template>
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length" class="team-expanded-cell">
                <div class="team-operators">
                  <div class="team-operators-header">
                    <div>
                      <strong>Usuários da equipe</strong>
                      <span>{{ item.users ? item.users.length : 0 }} usuário(s) vinculado(s)</span>
                    </div>
                    <v-btn small depressed color="primary" @click="openOperatorDialog(item)">
                      <v-icon left size="17">mdi-account-plus-outline</v-icon>
                      Adicionar usuário
                    </v-btn>
                  </div>

                  <div v-if="item.users && item.users.length" class="operator-list">
                    <div v-for="link in item.users" :key="link.id" class="operator-row">
                      <div>
                        <strong>{{ link.user ? link.user.name : "Usuário" }}</strong>
                        <span>{{ link.user ? link.user.email : "-" }}</span>
                      </div>
                      <v-chip x-small label :color="link.status === 'active' ? 'primary' : 'grey'" text-color="white">
                        {{ link.status === "active" ? "Ativo" : "Inativo" }}
                      </v-chip>
                      <v-btn icon small title="Remover usuário" @click="removeOperator(item, link)">
                        <v-icon size="18">mdi-trash-can-outline</v-icon>
                      </v-btn>
                    </div>
                  </div>

                  <div v-else class="operator-empty">
                    Nenhum usuário vinculado a esta equipe.
                  </div>
                </div>
              </td>
            </template>
            <template v-slot:footer.prepend>
              <v-btn icon small :loading="loadingTeams" title="Atualizar" @click="loadTeams">
                <v-icon size="19">mdi-refresh</v-icon>
              </v-btn>
            </template>
          </BoaruaDataTable>
        </section>
      </template>
    </div>

    <v-dialog v-model="teamDialog" max-width="620">
      <v-card class="form-dialog">
        <v-card-title>
          <div>
            <strong>{{ teamForm.id ? "Editar equipe" : "Nova equipe" }}</strong>
            <span>Defina equipe, núcleos atendidos e contato operacional.</span>
          </div>
        </v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12">
              <v-text-field v-model="teamForm.name" label="Nome da equipe" outlined dense hide-details="auto" />
            </v-col>
            <v-col cols="12">
              <v-select
                v-model="teamForm.contribution_group_ids"
                :items="groupOptions"
                label="Núcleos atendidos"
                outlined
                dense
                multiple
                chips
                small-chips
                clearable
                :disabled="teamForm.receive_all_alerts"
                hide-details="auto"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="teamForm.phone" v-mask="$masks.phone" label="Telefone" outlined dense hide-details="auto" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="teamForm.email" label="E-mail" outlined dense hide-details="auto" />
            </v-col>
            <v-col cols="12">
              <v-switch v-model="teamForm.receive_all_alerts" label="Receber alertas de todos os núcleos" color="primary" inset />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="teamDialog = false">Cancelar</v-btn>
          <v-btn depressed color="primary" :loading="savingTeam" @click="saveTeam">Salvar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="operatorDialog" max-width="560">
      <v-card class="form-dialog">
        <v-card-title>
          <div>
            <strong>Adicionar operador</strong>
            <span>Cadastre o operador que receberá chamados da equipe.</span>
          </div>
        </v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field v-model="operatorForm.name" label="Nome" outlined dense hide-details="auto" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="operatorForm.email" :rules="emailRules" label="E-mail de acesso" outlined dense hide-details="auto" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="operatorForm.phone" v-mask="$masks.phone" label="Celular" outlined dense hide-details="auto" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="operatorForm.document"
                v-mask="$masks.cpf"
                :rules="cpfRules"
                label="CPF"
                outlined
                dense
                hide-details="auto"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="operatorDialog = false">Cancelar</v-btn>
          <v-btn depressed color="primary" :loading="savingOperator" @click="saveOperator">Adicionar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteTeamDialog" max-width="460">
      <v-card class="form-dialog confirm-dialog">
        <v-card-title>
          <div>
            <strong>Excluir equipe</strong>
            <span>Esta ação remove a equipe da operação.</span>
          </div>
        </v-card-title>
        <v-card-text>
          <p>
            Deseja excluir a equipe
            <strong>{{ teamToDelete ? teamToDelete.name : "" }}</strong>?
          </p>
          <span>Ela ficará inativa e não receberá novos chamados.</span>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeDeleteTeamDialog">Cancelar</v-btn>
          <v-btn depressed color="error" :loading="deletingTeam" @click="confirmDeleteTeam">
            Excluir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import L from "leaflet";
import BoaruaDataTable from "@/views/boarua/components/BoaruaDataTable.vue";
import ListFilters from "@/views/boarua/components/ListFilters.vue";
import { applyListFilters, initialListFilters } from "@/views/boarua/helpers/listFilters";

export default {
  name: "BoaruaSecurityCenter",
  components: { BoaruaDataTable, ListFilters },
  data() {
    const currentUser = (this.$root.user && this.$root.user.data) || this.$root.user || {};
    const section = this.$route && this.$route.params ? this.$route.params.section : null;
    const sectionMap = { central: 0, territorio: 1, equipes: 2 };
    const initialSection = section || (Number(currentUser.type) === 5 ? "central" : "territorio");
    const initialTab = Object.prototype.hasOwnProperty.call(sectionMap, initialSection) ? sectionMap[initialSection] : 0;

    return {
      tab: initialTab,
      loadingAlerts: false,
      loadingTeams: false,
      statusFilter: "active",
      savingTeam: false,
      deletingTeam: false,
      savingOperator: false,
      savingTerritory: false,
      teamFilterDraft: initialListFilters(),
      activeTeamFilters: initialListFilters(),
      queueMinimized: false,
      soundEnabled: false,
      lastAlertIds: [],
      poll: null,
      alerts: [],
      teams: [],
      groups: [],
      association: null,
      alertsMap: null,
      territoryMap: null,
      alertMarkers: [],
      routeLayer: null,
      territoryPoints: [],
      territoryLayer: null,
      territoryMarkers: [],
      territorySaveTimer: null,
      territoryResizeObserver: null,
      drawingTerritory: true,
      addressSearch: "",
      addressResults: [],
      searchingAddress: false,
      teamDialog: false,
      operatorDialog: false,
      deleteTeamDialog: false,
      teamForm: this.initialTeamForm(),
      operatorForm: this.initialOperatorForm(),
      selectedTeam: null,
      teamToDelete: null,
      teamHeaders: [
        { text: "Equipe", value: "name" },
        { text: "Núcleos atendidos", value: "groups", sortable: false },
        { text: "Usuários", value: "users", sortable: false },
        { text: "Status", value: "status" },
        { text: "", value: "actions", sortable: false, align: "end", width: 118 },
      ],
      statusOptions: [
        { text: "Ativos", value: "active" },
        { text: "Novos", value: "new" },
        { text: "Em atendimento", value: "in_progress" },
        { text: "Em deslocamento", value: "on_route" },
        { text: "Resolvidos", value: "resolved" },
        { text: "Cancelados", value: "cancelled" },
        { text: "Todos", value: "all" },
      ],
      emailRules: [
        (value) => !!value || "Informe o e-mail",
        (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim()) || "E-mail inválido",
      ],
    };
  },
  computed: {
    activeSectionMeta() {
      const meta = [
        {
          title: "Central de segurança",
          description: "Acompanhe chamados em tempo real no mapa.",
        },
        {
          title: "Território de atendimento",
          description: "Busque endereços e desenhe o perímetro do local.",
        },
        {
          title: "Equipes de segurança",
          description: "Cadastre a equipe responsável pelo atendimento dos chamados.",
        },
      ];

      return meta[this.tab] || meta[0];
    },
    activeAlerts() {
      return this.alerts.filter((item) => ["new", "viewed", "in_progress", "on_route"].includes(String(item.status || "")));
    },
    displayAlerts() {
      return this.statusFilter === "active" ? this.activeAlerts : this.alerts;
    },
    groupOptions() {
      return this.groups.map((item) => ({ text: item.name, value: item.id }));
    },
    cpfRules() {
      return [
        (value) => !!this.onlyDigits(value) || "Informe o CPF",
        (value) => this.isValidCpf(value) || "CPF inválido",
      ];
    },
    teamFilterFields() {
      return [
        { name: "status", label: "Status", type: "select", items: [
          { text: "Ativa", value: "active" },
          { text: "Inativa", value: "inactive" },
        ] },
        { name: "receive_all_alerts", label: "Abrangência", type: "select", items: [
          { text: "Todos os núcleos", value: true },
          { text: "Núcleos específicos", value: false },
        ] },
      ];
    },
    filteredTeams() {
      return applyListFilters(this.teams, this.activeTeamFilters, {
        textKeys: ["name", "email", "phone", "status"],
        dateKeys: ["created_at", "updated_at"],
      });
    },
    mapCenter() {
      if (this.association && this.association.center_lat && this.association.center_lng) {
        return [Number(this.association.center_lat), Number(this.association.center_lng)];
      }
      const located = this.alerts.find((item) => item.latitude && item.longitude);
      if (located) return [Number(located.latitude), Number(located.longitude)];
      return [-30.0346, -51.2177];
    },
  },
  watch: {
    "$route.params.section"(value) {
      const nextTab = this.tabFromSection(value);
      if (nextTab !== this.tab) this.destroyInactiveMap(this.tab);
      this.tab = nextTab;
    },
    tab(value, oldValue) {
      this.destroyInactiveMap(oldValue);
      this.$nextTick(() => {
        if (this.tab === 0) this.initAlertsMap();
        if (this.tab === 1) this.initTerritoryMap();
      });
    },
  },
  async mounted() {
    await Promise.all([
      this.loadAssociation().catch(() => null),
      this.loadGroups().catch(() => null),
      this.loadTeams().catch(() => null),
      this.loadAlerts().catch(() => null),
    ]);
    this.$nextTick(() => {
      if (this.tab === 1) {
        this.initTerritoryMap();
      } else {
        this.initAlertsMap();
      }
    });
    this.poll = setInterval(this.loadAlerts, 8000);
    window.addEventListener("resize", this.handleMapLayoutChange);
    window.addEventListener("orientationchange", this.handleMapLayoutChange);
    window.addEventListener("boarua:layout-resize", this.handleMapLayoutChange);
  },
  beforeDestroy() {
    if (this.poll) clearInterval(this.poll);
    if (this.territorySaveTimer) clearTimeout(this.territorySaveTimer);
    window.removeEventListener("resize", this.handleMapLayoutChange);
    window.removeEventListener("orientationchange", this.handleMapLayoutChange);
    window.removeEventListener("boarua:layout-resize", this.handleMapLayoutChange);
    this.disconnectTerritoryResizeObserver();
    this.destroyAlertsMap();
    this.destroyTerritoryMap();
  },
  methods: {
    tabFromSection(section) {
      const map = { central: 0, territorio: 1, equipes: 2 };
      return Object.prototype.hasOwnProperty.call(map, section) ? map[section] : 0;
    },
    initialTeamForm() {
      return { name: "", contribution_group_ids: [], phone: "", email: "", receive_all_alerts: false, status: "active" };
    },
    initialOperatorForm() {
      return { name: "", email: "", phone: "", document: "" };
    },
    applyTeamFilters(filters) {
      this.activeTeamFilters = Object.assign(initialListFilters(), filters || {});
    },
    async loadAssociation() {
      const response = await axios.get("/associations/current/profile");
      this.association = response.data.content;
      this.territoryPoints = this.pointsFromGeoJson(this.association && this.association.territory_geojson);
    },
    async loadGroups() {
      const response = await axios.get("/contribution-groups", { params: { limit: 200 } });
      this.groups = response.data.content.data || response.data.content || [];
    },
    async loadTeams() {
      this.loadingTeams = true;
      try {
        const response = await axios.get("/security-teams", { params: { limit: 100 } });
        this.teams = response.data.content.data || response.data.content || [];
      } finally {
        this.loadingTeams = false;
      }
    },
    async loadAlerts() {
      this.loadingAlerts = true;
      try {
        const response = await axios.get("/security-alerts", {
          params: { limit: 100, search: JSON.stringify(this.alertSearch()) },
        });
        const items = response.data.content.data || response.data.content || [];
        this.notifyNewAlerts(items);
        this.alerts = items;
        this.renderAlertMarkers();
      } finally {
        this.loadingAlerts = false;
      }
    },
    alertSearch() {
      if (this.statusFilter === "active") return { active: true };
      if (this.statusFilter === "all") return {};
      return { status: this.statusFilter };
    },
    initAlertsMap() {
      if (!this.$refs.alertsMap) return;
      if (this.alertsMap && this.alertsMap._container !== this.$refs.alertsMap) {
        this.destroyAlertsMap();
      }
      if (!this.alertsMap) {
        this.alertsMap = L.map(this.$refs.alertsMap, { zoomControl: false }).setView(this.mapCenter, 14);
        L.control.zoom({ position: "bottomleft" }).addTo(this.alertsMap);
        this.addCleanMapLayer(this.alertsMap);
      }
      setTimeout(() => this.alertsMap.invalidateSize(), 120);
      this.renderAlertMarkers();
    },
    initTerritoryMap() {
      if (!this.$refs.territoryMap) return;
      if (this.territoryMap && this.territoryMap._container !== this.$refs.territoryMap) {
        this.destroyTerritoryMap();
      }
      if (!this.territoryMap) {
        this.territoryMap = L.map(this.$refs.territoryMap, { zoomControl: false }).setView(this.mapCenter, this.association && this.association.map_zoom ? this.association.map_zoom : 14);
        L.control.zoom({ position: "bottomleft" }).addTo(this.territoryMap);
        this.addCleanMapLayer(this.territoryMap);
        this.territoryMap.on("click", (event) => {
          if (!this.drawingTerritory) return;
          this.addTerritoryPoint(event.latlng.lat, event.latlng.lng);
        });
      }
      this.refreshTerritoryMapSize();
      this.renderTerritory(true);
      this.observeTerritoryMapContainer();
    },
    destroyInactiveMap(tab) {
      if (tab === 0) this.destroyAlertsMap();
      if (tab === 1) this.destroyTerritoryMap();
    },
    destroyAlertsMap() {
      if (this.alertsMap) {
        this.alertsMap.remove();
      }
      this.alertsMap = null;
      this.alertMarkers = [];
      this.routeLayer = null;
    },
    destroyTerritoryMap() {
      this.disconnectTerritoryResizeObserver();
      if (this.territoryMap) {
        this.territoryMap.remove();
      }
      this.territoryMap = null;
      this.territoryLayer = null;
      this.territoryMarkers = [];
    },
    addCleanMapLayer(map) {
      L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
        attribution: "&copy; OpenStreetMap &copy; CARTO",
        subdomains: "abcd",
        maxZoom: 20,
      }).addTo(map);
    },
    refreshTerritoryMapSize() {
      if (!this.territoryMap) return;
      [0, 80, 180, 360, 720, 1100].forEach((delay) => {
        setTimeout(() => {
          if (!this.territoryMap) return;
          this.territoryMap.invalidateSize({ pan: false });
          if (this.territoryLayer && this.territoryPoints.length) {
            this.territoryMap.fitBounds(this.territoryLayer.getBounds(), { padding: [34, 34], animate: false });
          }
        }, delay);
      });
    },
    observeTerritoryMapContainer() {
      if (!this.$refs.territoryMap || !window.ResizeObserver || this.territoryResizeObserver) return;
      this.territoryResizeObserver = new ResizeObserver(() => this.refreshTerritoryMapSize());
      this.territoryResizeObserver.observe(this.$refs.territoryMap);
    },
    disconnectTerritoryResizeObserver() {
      if (this.territoryResizeObserver) {
        this.territoryResizeObserver.disconnect();
      }
      this.territoryResizeObserver = null;
    },
    handleMapLayoutChange() {
      if (this.tab === 0 && this.alertsMap) {
        setTimeout(() => this.alertsMap && this.alertsMap.invalidateSize({ pan: false }), 120);
      }
      if (this.tab === 1) {
        this.refreshTerritoryMapSize();
      }
    },
    renderAlertMarkers() {
      if (!this.alertsMap) return;
      this.alertMarkers.forEach((marker) => marker.remove());
      this.alertMarkers = [];

      this.activeAlerts.forEach((alert) => {
        if (!alert.latitude || !alert.longitude) return;
        const marker = L.marker([Number(alert.latitude), Number(alert.longitude)], {
          icon: L.divIcon({ className: `alert-pin ${alert.priority}`, html: "<span></span>", iconSize: [26, 26] }),
        })
          .addTo(this.alertsMap)
          .bindPopup(`<strong>${alert.title}</strong><br>${alert.location_address || "Sem endereço"}<br>${this.statusLabel(alert.status)}`);
        this.alertMarkers.push(marker);
      });
    },
    addTerritoryPoint(lat, lng) {
      this.territoryPoints.push([lat, lng]);
      this.renderTerritory(false);
      this.scheduleTerritorySave();
    },
    renderTerritory(fit = false) {
      if (!this.territoryMap) return;
      if (this.territoryLayer) this.territoryLayer.remove();
      this.clearTerritoryMarkers();
      if (!this.territoryPoints.length) return;

      const layerFactory = this.territoryPoints.length > 2 ? L.polygon : L.polyline;
      this.territoryLayer = layerFactory(this.territoryPoints, {
        color: "#04753d",
        fillColor: "#057a3d",
        fillOpacity: this.territoryPoints.length > 2 ? 0.16 : 0,
        opacity: 0.92,
        weight: 3,
      }).addTo(this.territoryMap);

      this.territoryPoints.forEach((point, index) => {
        const marker = L.marker(point, {
          draggable: true,
          icon: L.divIcon({
            className: "territory-point-marker",
            html: `<span>${index + 1}</span>`,
            iconSize: [26, 26],
          }),
        }).addTo(this.territoryMap);

        marker.on("dragend", (event) => {
          const latLng = event.target.getLatLng();
          this.$set(this.territoryPoints, index, [latLng.lat, latLng.lng]);
          this.renderTerritory(false);
          this.scheduleTerritorySave();
        });

        this.territoryMarkers.push(marker);
      });

      if (fit) this.fitTerritory();
    },
    clearTerritoryMarkers() {
      this.territoryMarkers.forEach((marker) => marker.remove());
      this.territoryMarkers = [];
    },
    undoTerritoryPoint() {
      this.territoryPoints.pop();
      this.renderTerritory(false);
      this.scheduleTerritorySave();
    },
    fitTerritory() {
      if (!this.territoryMap || !this.territoryLayer || !this.territoryPoints.length) return;
      this.territoryMap.fitBounds(this.territoryLayer.getBounds(), { padding: [34, 34] });
    },
    clearTerritory() {
      this.territoryPoints = [];
      if (this.territoryLayer) this.territoryLayer.remove();
      this.territoryLayer = null;
      this.clearTerritoryMarkers();
      this.scheduleTerritorySave();
    },
    async searchAddress() {
      const query = String(this.addressSearch || "").trim();
      if (!query) return;

      this.searchingAddress = true;
      try {
        const results = [];
        const seen = new Set();
        const searches = this.addressSearchCandidates(query);

        for (const search of searches) {
          const response = await axios.get("/geocoding/search", {
            params: { q: search, limit: 8 },
          });
          const items = response.data.content || [];

          (Array.isArray(items) ? items : []).forEach((item) => {
            const key = item.place_id || `${item.lat},${item.lon}`;
            if (!seen.has(key)) {
              seen.add(key);
              results.push(item);
            }
          });
        }

        this.addressResults = this.rankAddressResults(results, query).slice(0, 6);
        if (!this.addressResults.length) {
          this.notifyError("Endereço não encontrado");
        }
      } catch (error) {
        this.notifyError("Não foi possível buscar o endereço");
      } finally {
        this.searchingAddress = false;
      }
    },
    addressSearchCandidates(query) {
      const city = this.association && this.association.city ? String(this.association.city).trim() : "";
      const state = this.association && this.association.state ? String(this.association.state).trim() : "";
      const candidates = [
        query,
        this.restoreCommonAddressAccents(query),
      ];

      [query, this.restoreCommonAddressAccents(query)].forEach((item) => {
        if (city && state) candidates.push(`${item}, ${city}, ${state}, Brasil`);
        else if (city) candidates.push(`${item}, ${city}, Brasil`);
        else candidates.push(`${item}, Brasil`);
      });

      return [...new Set(candidates.filter(Boolean))];
    },
    restoreCommonAddressAccents(value) {
      return String(value || "")
        .replace(/\babaete\b/gi, (match) => this.matchCase(match, "abaeté"));
    },
    matchCase(original, replacement) {
      if (original === original.toUpperCase()) return replacement.toUpperCase();
      if (original.charAt(0) === original.charAt(0).toUpperCase()) {
        return replacement.charAt(0).toUpperCase() + replacement.slice(1);
      }
      return replacement;
    },
    rankAddressResults(results, query) {
      const normalizedQuery = this.normalizeSearchText(query);
      const city = this.normalizeSearchText(this.association && this.association.city);
      const state = this.normalizeSearchText(this.association && this.association.state);
      const [centerLat, centerLng] = this.territoryMap ? [this.territoryMap.getCenter().lat, this.territoryMap.getCenter().lng] : this.mapCenter;

      return results.sort((a, b) => {
        return this.addressResultScore(b, normalizedQuery, city, state, centerLat, centerLng)
          - this.addressResultScore(a, normalizedQuery, city, state, centerLat, centerLng);
      });
    },
    addressResultScore(result, normalizedQuery, city, state, centerLat, centerLng) {
      const text = this.normalizeSearchText(result && result.display_name);
      const lat = Number(result && result.lat);
      const lng = Number(result && result.lon);
      let score = 0;

      if (text.includes(normalizedQuery)) score += 80;
      normalizedQuery.split(" ").filter(Boolean).forEach((part) => {
        if (text.includes(part)) score += 12;
      });
      if (city && text.includes(city)) score += 70;
      if (state && text.includes(state)) score += 25;

      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        const distance = Math.sqrt(Math.pow(lat - centerLat, 2) + Math.pow(lng - centerLng, 2));
        score += Math.max(0, 40 - distance * 100);
      }

      return score;
    },
    normalizeSearchText(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
    },
    moveToAddress(result) {
      if (!this.territoryMap || !result) return;
      const lat = Number(result.lat);
      const lng = Number(result.lon);
      this.territoryMap.flyTo([lat, lng], 16, { duration: 0.7 });
      L.circleMarker([lat, lng], {
        radius: 8,
        color: "#057a3d",
        fillColor: "#057a3d",
        fillOpacity: 0.2,
        weight: 2,
      }).addTo(this.territoryMap);
    },
    scheduleTerritorySave() {
      if (this.territorySaveTimer) clearTimeout(this.territorySaveTimer);

      this.territorySaveTimer = setTimeout(() => {
        if (!this.territoryPoints.length || this.territoryPoints.length >= 3) {
          this.saveTerritory(true);
        }
      }, 800);
    },
    async saveTerritory(silent = false) {
      if (this.territoryPoints.length > 0 && this.territoryPoints.length < 3) {
        if (!silent) this.notifyError("Marque pelo menos 3 pontos no mapa");
        return;
      }

      this.savingTerritory = true;
      try {
        const isClearing = !this.territoryPoints.length;
        const center = !isClearing && this.territoryLayer ? this.territoryLayer.getBounds().getCenter() : null;
        const coordinates = isClearing ? [] : this.territoryPoints.map(([lat, lng]) => [lng, lat]);
        if (coordinates.length) coordinates.push(coordinates[0]);
        const response = await axios.patch("/associations/current/profile", {
          territory_geojson: isClearing ? null : JSON.stringify({ type: "Polygon", coordinates: [coordinates] }),
          center_lat: center ? center.lat : null,
          center_lng: center ? center.lng : null,
          map_zoom: this.territoryMap ? this.territoryMap.getZoom() : null,
        });
        this.association = response.data.content;
        if (!silent) this.notifySuccess("Território salvo");
      } catch (error) {
        if (!silent) this.notifyError("Não foi possível salvar o território");
      } finally {
        this.savingTerritory = false;
      }
    },
    pointsFromGeoJson(value) {
      if (!value) return [];
      try {
        const geo = typeof value === "string" ? JSON.parse(value) : value;
        const coords = geo.type === "Feature" ? geo.geometry.coordinates[0] : geo.coordinates[0];
        return coords.slice(0, -1).map(([lng, lat]) => [lat, lng]);
      } catch (error) {
        return [];
      }
    },
    openTeamDialog(item = null) {
      this.teamForm = item
        ? Object.assign({}, item, { contribution_group_ids: this.teamGroupIds(item) })
        : this.initialTeamForm();
      this.teamDialog = true;
    },
    openOperatorDialog(team) {
      this.selectedTeam = team;
      this.operatorForm = this.initialOperatorForm();
      this.operatorDialog = true;
    },
    async saveTeam() {
      this.savingTeam = true;
      try {
        const payload = Object.assign({}, this.teamForm, {
          contribution_group_ids: this.teamForm.receive_all_alerts ? [] : (this.teamForm.contribution_group_ids || []),
        });
        if (this.teamForm.id) {
          await axios.patch(`/security-teams/${this.teamForm.id}`, payload);
        } else {
          await axios.post("/security-teams", payload);
        }
        this.teamDialog = false;
        await this.loadTeams();
        this.notifySuccess("Equipe salva");
      } catch (error) {
        this.notifyError("Não foi possível salvar a equipe");
      } finally {
        this.savingTeam = false;
      }
    },
    openDeleteTeamDialog(team) {
      if (!team || !team.id) return;
      this.teamToDelete = team;
      this.deleteTeamDialog = true;
    },
    closeDeleteTeamDialog() {
      if (this.deletingTeam) return;
      this.deleteTeamDialog = false;
      this.teamToDelete = null;
    },
    async confirmDeleteTeam() {
      if (!this.teamToDelete || !this.teamToDelete.id) return;

      this.deletingTeam = true;
      try {
        await axios.delete(`/security-teams/${this.teamToDelete.id}`);
        this.deleteTeamDialog = false;
        this.teamToDelete = null;
        await this.loadTeams();
        this.notifySuccess("Equipe excluída");
      } catch (error) {
        this.notifyError("Não foi possível excluir a equipe");
      } finally {
        this.deletingTeam = false;
      }
    },
    teamGroups(team) {
      const links = team && team.groups ? team.groups : [];
      return links
        .map((item) => item.group || null)
        .filter(Boolean);
    },
    teamGroupIds(team) {
      if (team && team.groups && team.groups.length) {
        return team.groups
          .map((item) => Number(item.contribution_group_id))
          .filter((id) => Number.isInteger(id) && id > 0);
      }

      return team && team.contribution_group_id ? [Number(team.contribution_group_id)] : [];
    },
    async removeOperator(team, link) {
      if (!team || !link) return;

      try {
        await axios.delete(`/security-teams/${team.id}/users/${link.id}`);
        await this.loadTeams();
        this.notifySuccess("Usuário removido da equipe");
      } catch (error) {
        this.notifyError("Não foi possível remover o usuário");
      }
    },
    async saveOperator() {
      if (!this.selectedTeam || !this.operatorForm.email) {
        this.notifyError("Informe o e-mail do operador");
        return;
      }
      if (!this.isValidEmail(this.operatorForm.email)) {
        this.notifyError("Informe um e-mail válido");
        return;
      }
      if (!this.isValidCpf(this.operatorForm.document)) {
        this.notifyError("Informe um CPF válido para o operador");
        return;
      }

      this.savingOperator = true;
      try {
        const document = this.onlyDigits(this.operatorForm.document);
        const payload = {
          name: this.operatorForm.name,
          email: String(this.operatorForm.email || "").trim().toLowerCase(),
          phone: this.onlyDigits(this.operatorForm.phone),
          document,
          password: document,
        };

        await axios.post(`/security-teams/${this.selectedTeam.id}/users`, payload);
        this.operatorDialog = false;
        await this.loadTeams();
        this.notifySuccess("Operador adicionado");
      } catch (error) {
        this.notifyError("Não foi possível adicionar o operador");
      } finally {
        this.savingOperator = false;
      }
    },
    isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
    },
    isValidCpf(value) {
      const document = this.onlyDigits(value);
      if (!document || document.length !== 11 || /^(\d)\1+$/.test(document)) return false;

      const calculateDigit = (base) => {
        let factor = base.length + 1;
        const total = base.split("").reduce((sum, number) => {
          const current = sum + Number(number) * factor;
          factor -= 1;
          return current;
        }, 0);
        const digit = 11 - (total % 11);
        return digit >= 10 ? 0 : digit;
      };

      const firstDigit = calculateDigit(document.slice(0, 9));
      const secondDigit = calculateDigit(`${document.slice(0, 9)}${firstDigit}`);

      return document.endsWith(`${firstDigit}${secondDigit}`);
    },
    onlyDigits(value) {
      return String(value || "").replace(/\D/g, "");
    },
    async assign(alert) {
      await axios.post(`/security-alerts/${alert.id}/assign`);
      await this.loadAlerts();
    },
    async setStatus(alert, status) {
      await axios.post(`/security-alerts/${alert.id}/status`, { status });
      await this.loadAlerts();
    },
    async drawRoute(alert) {
      if (!navigator.geolocation) {
        this.notifyError("Geolocalização não disponível neste navegador");
        return;
      }

      navigator.geolocation.getCurrentPosition(async (position) => {
        try {
          const response = await axios.post(`/security-alerts/${alert.id}/route`, {
            origin: {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            },
          });

          const route = response.data.content && response.data.content.routes && response.data.content.routes[0];
          const coordinates = route && route.geometry && route.geometry.coordinates
            ? route.geometry.coordinates.map(([lng, lat]) => [lat, lng])
            : [];

          if (!coordinates.length) {
            this.notifyError("Rota não encontrada");
            return;
          }

          if (this.routeLayer) this.routeLayer.remove();
          this.routeLayer = L.polyline(coordinates, { color: "#057a3d", weight: 5, opacity: 0.82 }).addTo(this.alertsMap);
          this.alertsMap.fitBounds(this.routeLayer.getBounds(), { padding: [30, 30] });
        } catch (error) {
          this.notifyError("Não foi possível calcular a rota");
        }
      }, () => this.notifyError("Autorize sua localização para calcular a rota"));
    },
    enableSound() {
      this.soundEnabled = true;
      this.beep();
    },
    notifyNewAlerts(items) {
      const ids = items.map((item) => item.id);
      const hasNew = this.lastAlertIds.length && items.some((item) => item.status === "new" && !this.lastAlertIds.includes(item.id));
      this.lastAlertIds = ids;
      if (hasNew && this.soundEnabled) this.beep();
    },
    beep() {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      const context = new AudioContext();
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.frequency.value = 880;
      gain.gain.value = 0.08;
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start();
      setTimeout(() => {
        oscillator.stop();
        context.close();
      }, 220);
    },
    statusLabel(status) {
      const labels = {
        new: "Novo",
        viewed: "Visualizado",
        in_progress: "Em atendimento",
        on_route: "Em deslocamento",
        resolved: "Resolvido",
        cancelled: "Cancelado",
        false_alarm: "Falso alarme",
      };
      return labels[status] || status || "-";
    },
    notifySuccess(message) {
      if (this.$dialog && this.$dialog.notify) this.$dialog.notify.success(message, { position: "top-right", timeout: 3000 });
    },
    notifyError(message) {
      if (this.$dialog && this.$dialog.notify) this.$dialog.notify.error(message, { position: "top-right", timeout: 4000 });
    },
  },
};
</script>

<style scoped lang="scss">
.resource-toolbar,
.panel-heading,
.queue-header,
.alert-top,
.toolbar-actions {
  display: flex;
  align-items: center;
}

.resource-toolbar,
.panel-heading,
.queue-header {
  justify-content: space-between;
  gap: 18px;
}

.resource-toolbar {
  margin-bottom: 14px;

  h2,
  p {
    margin: 0;
  }

  h2 {
    color: #111827;
    font-size: 22px;
  }

  p {
    margin-top: 3px;
    color: #66736b;
    font-size: 13px;
  }
}

.toolbar-actions {
  gap: 10px;
}

.status-filter {
  width: 190px;
}

.security-tabs {
  margin-bottom: 12px;
}

.security-page.central-focus,
.security-page.territory-focus {
  .resource-toolbar {
    display: none;
  }

  .security-tabs {
    position: fixed;
    z-index: 430;
    top: 18px;
    left: calc(var(--boarua-sidebar-width, 270px) + ((100vw - var(--boarua-sidebar-width, 270px)) / 2));
    transform: translateX(-50%);
    width: auto;
    padding: 4px;
    background: rgba(255, 255, 255, 0.92);
    border: 1px solid rgba(227, 233, 228, 0.88);
    border-radius: 999px;
    box-shadow: 0 18px 44px rgba(17, 24, 39, 0.12);
    backdrop-filter: blur(12px);

    ::v-deep .v-tabs-bar {
      height: 38px;
      background: transparent;
    }

    ::v-deep .v-tab {
      min-width: 104px;
      min-height: 38px;
      border-radius: 999px;
      color: #66736b;
      font-size: 12px;
      font-weight: 800;
      letter-spacing: 0;
      text-transform: none;
    }

    ::v-deep .v-tab--active {
      color: #ffffff;
      background: #057a3d;
    }

    ::v-deep .v-tabs-slider-wrapper {
      display: none;
    }
  }

  .tabs-body {
    overflow: visible;
  }
}

.security-page.central-focus {
  .central-grid {
    position: fixed;
    z-index: 420;
    inset: 72px 0 0 var(--boarua-sidebar-width, 270px);
    display: block;
    background: #edf2ef;
  }

  .map-panel {
    position: absolute;
    inset: 0;
    border-radius: 0;
    overflow: hidden;
  }

  .map {
    width: 100%;
    height: 100%;
    min-height: calc(100vh - 72px);
    border-radius: 0;
  }

  .queue-panel {
    position: absolute;
    z-index: 425;
    top: 24px;
    right: 24px;
    bottom: 24px;
    width: min(390px, calc(100% - 48px));
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: auto;
    border: 1px solid rgba(227, 233, 228, 0.88);
    border-radius: 18px;
    box-shadow: 0 22px 50px rgba(17, 24, 39, 0.14);
    transition: width 180ms ease, bottom 180ms ease, border-radius 180ms ease;
  }

  .queue-panel.minimized {
    bottom: auto;
    width: auto;
    min-height: 0;
    padding: 0;
    overflow: visible;
    background: transparent;
    border: 0;
    border-radius: 0;
    box-shadow: none;
  }

  .empty-queue {
    flex: 1;
    min-height: 260px;
  }
}

.security-page.territory-focus {

  .territory-map-stage {
    position: fixed;
    z-index: 420;
    top: 72px;
    right: 0;
    bottom: 0;
    left: var(--boarua-sidebar-width, 270px);
    height: auto;
    min-height: 0;
    margin: 0;
    overflow: hidden;
    background: #edf2ef;
  }

  .territory-map-wrap,
  .territory-map {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    min-height: 0;
    border-radius: 0;
  }

  .territory-map {
    box-shadow: none;
  }

  .map-title-control {
    top: 78px;
    left: 24px;
    width: min(560px, calc(100% - 148px));
    padding: 14px 16px;
  }

  .map-search-control {
    top: clamp(14px, 3.2vh, 32px);
    left: clamp(14px, 2.8vw, 34px);
    width: min(470px, calc(100% - 112px));
  }

  .map-tool-stack {
    top: clamp(14px, 3.2vh, 32px);
    right: clamp(14px, 2.8vw, 34px);
  }

  .map-points-chip {
    right: clamp(14px, 2.8vw, 34px);
    bottom: clamp(14px, 2.8vh, 24px);
  }

  .map-floating-hint {
    left: clamp(14px, 2.8vw, 34px);
    bottom: clamp(14px, 2.8vh, 24px);
  }
}

.tabs-body {
  background: transparent;
}

.central-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 14px;
}

.map-panel,
.queue-panel,
.teams-panel {
  background: #ffffff;
  border-radius: 14px;
}

.map-panel {
  position: relative;
  overflow: hidden;
}

.map,
.territory-map {
  min-height: 610px;
  border-radius: 14px;
  overflow: hidden;
}

.queue-panel {
  min-height: 610px;
  padding: 18px;
}

.queue-header {
  margin-bottom: 14px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;

  strong,
  span {
    display: block;
  }

  span {
    color: #66736b;
    font-size: 12px;
  }
}

.queue-header-actions {
  display: flex;
  align-items: center;
  gap: 2px;
}

.queue-minimized-button {
  height: 46px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #111827;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(227, 233, 228, 0.9);
  border-radius: 999px;
  box-shadow: 0 14px 32px rgba(17, 24, 39, 0.12);
  cursor: pointer;

  span {
    font-size: 12px;
    font-weight: 900;
  }

  strong {
    min-width: 22px;
    height: 22px;
    padding: 0 7px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    background: #057a3d;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 900;
  }
}

.queue-controls {
  margin-bottom: 14px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
}

.queue-status-filter {
  min-width: 0;
}

.queue-sound-btn {
  min-width: 92px;
}

.alert-card {
  margin-bottom: 10px;
  padding: 14px;
  border: 1px solid #e3e9e4;
  border-left: 4px solid #057a3d;
  border-radius: 12px;

  &.priority-critical,
  &.priority-high {
    border-left-color: #e53935;
  }

  p {
    margin: 7px 0 3px;
    color: #66736b;
    font-size: 13px;
  }

  small {
    color: #8a948e;
  }
}

.alert-top {
  justify-content: space-between;
  gap: 12px;

  span {
    color: #057a3d;
    font-size: 11px;
    font-weight: 800;
  }
}

.alert-actions {
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.empty-queue {
  min-height: 380px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  text-align: center;

  span {
    color: #66736b;
    font-size: 13px;
  }
}

.central-map-badge {
  position: absolute;
  z-index: 403;
  left: 22px;
  top: 18px;
  padding: 7px 10px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #111827;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(227, 233, 228, 0.86);
  border-radius: 999px;
  box-shadow: 0 10px 24px rgba(17, 24, 39, 0.08);
  font-size: 12px;
  font-weight: 800;
}

.teams-panel {
  padding: 18px;
}

.team-groups {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;

  span {
    color: #66736b;
    font-size: 12px;
  }
}

.team-expanded-cell {
  padding: 0 !important;
  background: #f7faf8;
}

.team-operators {
  margin: 0;
  padding: 16px 18px;
  border-top: 1px solid #e3e9e4;
}

.team-operators-header,
.operator-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.team-operators-header {
  margin-bottom: 12px;

  strong,
  span {
    display: block;
  }

  span {
    color: #66736b;
    font-size: 12px;
  }
}

.operator-list {
  display: grid;
  gap: 8px;
}

.operator-row {
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid #e3e9e4;
  border-radius: 10px;

  div {
    min-width: 0;
    flex: 1;
  }

  strong,
  span {
    display: block;
  }

  strong {
    color: #111827;
    font-size: 13px;
  }

  span {
    color: #66736b;
    font-size: 12px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.operator-empty {
  padding: 14px;
  color: #66736b;
  background: #ffffff;
  border: 1px dashed #d6ded8;
  border-radius: 10px;
  font-size: 13px;
  text-align: center;
}

.territory-map-stage {
  background: transparent;
}

  .territory-map-wrap {
    position: relative;
    min-width: 0;
  }

  .territory-map {
    min-height: calc(100vh - 250px);
    border-radius: 18px;
    box-shadow: inset 0 0 0 1px #e3e9e4;
  }

.map-title-control {
  width: min(520px, calc(100% - 128px));
  position: absolute;
  z-index: 403;
  left: 18px;
  top: 18px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  color: #111827;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid #e3e9e4;
  border-radius: 16px;
  box-shadow: 0 16px 38px rgba(17, 24, 39, 0.1);

  strong,
  span {
    display: block;
  }

  strong {
    font-size: 15px;
    font-weight: 900;
  }

  span {
    margin-top: 3px;
    color: #66736b;
    font-size: 12px;
  }
}

.map-search-control {
  width: min(430px, calc(100% - 120px));
  position: absolute;
  z-index: 402;
  left: 18px;
  top: 98px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 42px;
  align-items: center;
  gap: 6px;
  padding: 6px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #e3e9e4;
  border-radius: 14px;
  box-shadow: 0 14px 36px rgba(17, 24, 39, 0.1);
}

.floating-results {
  grid-column: 1 / -1;
  max-height: 230px;
  overflow: auto;
  padding-top: 4px;

  button {
    width: 100%;
    padding: 10px;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    color: #111827;
    background: #ffffff;
    border: 0;
    border-top: 1px solid #edf0ed;
    text-align: left;

    span {
      color: #66736b;
      display: -webkit-box;
      font-size: 12px;
      line-height: 1.35;
      overflow: hidden;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
    }
  }
}

.map-tool-stack {
  position: absolute;
  z-index: 402;
  right: 18px;
  top: 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  button {
    width: 46px;
    height: 46px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #267062;
    background: rgba(255, 255, 255, 0.96);
    border: 1px solid #e3e9e4;
    border-radius: 13px;
    box-shadow: 0 12px 30px rgba(17, 24, 39, 0.08);

    &.active {
      color: #ffffff;
      background: #057a3d;
      border-color: #057a3d;
    }

    &.danger {
      color: #b91c1c;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.45;
    }
  }
}

.map-points-chip {
  position: absolute;
  z-index: 402;
  right: 18px;
  bottom: 18px;
  min-width: 84px;
  padding: 9px 12px;
  color: #267062;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid #e3e9e4;
  border-radius: 999px;
  box-shadow: 0 12px 30px rgba(17, 24, 39, 0.08);
  text-align: center;

  strong,
  span {
    display: inline;
    font-size: 12px;
    font-weight: 900;
  }
}

.map-floating-hint {
  max-width: min(520px, calc(100% - 32px));
  position: absolute;
  z-index: 401;
  left: 18px;
  bottom: 18px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #111827;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid #e3e9e4;
  border-radius: 12px;
  box-shadow: 0 12px 30px rgba(17, 24, 39, 0.08);
  font-size: 13px;
}

@media (max-width: 1180px) and (min-width: 960px) {
  .security-page.central-focus {
    .central-grid {
      top: 72px;
      left: var(--boarua-sidebar-width, 72px);
      right: 0;
      bottom: 0;
    }
  }

  .security-page.territory-focus {
    .territory-map-stage {
      top: 72px;
      left: var(--boarua-sidebar-width, 72px);
      right: 0;
      bottom: 0;
    }

    .map-search-control {
      width: min(430px, calc(100% - 94px));
    }

    .map-tool-stack button {
      width: 42px;
      height: 42px;
    }

    .map-floating-hint {
      max-width: min(510px, calc(100% - 150px));
    }
  }
}

@media (max-width: 720px) {
  .security-page.territory-focus {
    .territory-map-stage {
      top: 72px;
      left: 0;
    }

    .map-search-control {
      right: 14px;
      width: auto;
    }

    .map-tool-stack {
      top: 80px;
    }

    .map-floating-hint {
      right: 14px;
      max-width: none;
      font-size: 12px;
    }

    .map-points-chip {
      bottom: 72px;
    }
  }
}

.panel-heading {
  margin-bottom: 14px;

  h3,
  p {
    margin: 0;
  }

  h3 {
    color: #111827;
    font-size: 20px;
  }

  p {
    margin-top: 3px;
    color: #66736b;
    font-size: 13px;
  }
}

.form-dialog {
  border-radius: 12px;
}

::v-deep .alert-pin {
  position: relative;

  span {
    position: absolute;
    width: 18px;
    height: 18px;
    left: 4px;
    top: 4px;
    background: #e53935;
    border: 3px solid #ffffff;
    border-radius: 50%;
    box-shadow: 0 0 0 rgba(229, 57, 53, 0.55);
    animation: pulse-alert 1.2s infinite;
  }
}

::v-deep .territory-point-marker {
  span {
    width: 26px;
    height: 26px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    background: #057a3d;
    border: 3px solid #ffffff;
    border-radius: 50%;
    box-shadow: 0 6px 14px rgba(5, 122, 61, 0.28);
    font-size: 11px;
    font-weight: 900;
  }
}

@keyframes pulse-alert {
  0% {
    box-shadow: 0 0 0 0 rgba(229, 57, 53, 0.55);
  }
  70% {
    box-shadow: 0 0 0 16px rgba(229, 57, 53, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(229, 57, 53, 0);
  }
}

@media (max-width: 1100px) {
  .central-grid {
    grid-template-columns: 1fr;
  }

  .security-page.central-focus,
  .security-page.territory-focus {
    .security-tabs {
      left: 50%;
    }
  }

}

@media (max-width: 959px) {
  .security-page.central-focus {
    .central-grid {
      inset: 72px 0 0 0;
    }

    .map {
      min-height: calc(100vh - 72px);
    }
  }

  .security-page.territory-focus {
    .territory-map-stage {
      inset: 72px 0 0 0;
      height: auto;
      min-height: 0;
      margin: 0;
    }

    .territory-map-wrap,
    .territory-map {
      min-height: 0;
    }
  }
}

@media (max-width: 720px) {
  .security-page.central-focus,
  .security-page.territory-focus {
    .security-tabs {
      top: 10px;
      max-width: calc(100vw - 20px);
      overflow: hidden;

      ::v-deep .v-tab {
        min-width: 88px;
        padding: 0 12px;
      }
    }
  }

  .security-page.central-focus {
    .central-map-badge {
      top: 12px;
      left: 12px;
    }

    .queue-panel {
      top: auto;
      right: 12px;
      bottom: 12px;
      left: 12px;
      width: auto;
      max-height: 42vh;
      border-radius: 18px;
    }

    .queue-panel.minimized {
      top: auto;
      right: 12px;
      bottom: 12px;
      left: auto;
      width: auto;
      max-height: none;
    }

    .queue-controls {
      grid-template-columns: 1fr;
    }
  }

  .security-page.territory-focus {
    .map-title-control {
      top: 12px;
      left: 12px;
      width: calc(100% - 24px);
      align-items: flex-start;
      flex-direction: column;
    }

    .map-search-control {
      top: 14px;
      left: 14px;
      width: calc(100% - 86px);
    }

    .map-tool-stack {
      top: 14px;
      right: 14px;
    }

    .map-floating-hint {
      left: 14px;
      right: 14px;
      bottom: 14px;
      max-width: none;
    }

    .map-points-chip {
      right: 14px;
      bottom: 78px;
    }
  }
}
</style>
